/**
 * Stand statistics from a tree GeoPackage, computed in the browser for every viewer of the
 * report (N13). Pure: the sections only format what comes out of here.
 */
import type { FeatureCollection } from 'geojson';
import { genusOf, TREE_FIELDS } from '@/map/layers';
import type { Threshold } from './config';
import { areaOf, centroidOf, type Area } from './geometry';
import { maturity, thresholdsFor, type MaturityStats, type Target } from './maturity';
import type { Metric, Stratum, Tree } from './types';

export const NON_SPECIES = new Set(['Unknown', 'Snag', 'Shrub']);

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const mean = (xs: number[]): number | null => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);
const perHa = (value: number, area: Area): number | null => (area.ha > 0 ? value / area.ha : null);

export function normalize(fc: FeatureCollection): Tree[] {
  const out: Tree[] = [];
  fc.features.forEach((f, i) => {
    const c = centroidOf(f.geometry);
    if (!c) return;
    const p = f.properties ?? {};
    const genus = genusOf(p[TREE_FIELDS.genus]) || 'Unknown';
    const dbh = num(p[TREE_FIELDS.dbh.prop]);
    // forun writes a few volumes negative (allometry below its fit range); the magnitude is meant.
    const volume = num(p[TREE_FIELDS.volume.prop]);
    out.push({
      id: String(f.id ?? i + 1),
      genus,
      isSpecies: !NON_SPECIES.has(genus),
      height: num(p[TREE_FIELDS.height.prop]),
      dbhCm: dbh === null ? null : dbh * TREE_FIELDS.dbh.scale,
      volume: volume === null ? null : Math.abs(volume),
      lon: c[0],
      lat: c[1],
    });
  });
  return out;
}

/** Min and max by loop: spreading 100k+ values into Math.min throws a RangeError. */
const perTrees = new WeakMap<Tree[], Map<string, unknown>>();

/** `compute` once per list of trees and key: the analysis tabs come and go, the stand stays. */
export function once<T>(trees: Tree[], key: string, compute: () => T): T {
  let cache = perTrees.get(trees);
  if (!cache) perTrees.set(trees, (cache = new Map()));
  if (!cache.has(key)) cache.set(key, compute());
  return cache.get(key) as T;
}

/** The range of a metric over the trees that have it; [0, 1] when none has. */
export function metricRange(trees: Tree[], metric: Metric): [number, number] {
  return once(trees, `range:${metric}`, () => {
    const vs = trees.map((x) => metricOf(x, metric)).filter((v): v is number => v !== null);
    return vs.length ? extent(vs) : [0, 1];
  });
}

export function extent(values: number[]): [number, number] {
  let lo = Infinity;
  let hi = -Infinity;
  for (const v of values) {
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  return [lo, hi];
}

export const metricOf = (t: Tree, m: Metric): number | null => (m === 'height' ? t.height : m === 'dbh' ? t.dbhCm : t.volume);
const valuesOf = (trees: Tree[], m: Metric) => trees.map((t) => metricOf(t, m)).filter((v): v is number => v !== null);
const sumVolume = (trees: Tree[]) => trees.reduce((s, t) => s + (t.volume ?? 0), 0);

export interface Summary {
  count: number;
  volume: number;
  volumePerHa: number | null;
  meanDbh: number | null;
  meanHeight: number | null;
}

export function summary(trees: Tree[], area: Area): Summary {
  const volume = sumVolume(trees);
  return { count: trees.length, volume, volumePerHa: perHa(volume, area), meanDbh: mean(valuesOf(trees, 'dbh')), meanHeight: mean(valuesOf(trees, 'height')) };
}

export interface SpeciesRow {
  genus: string;
  isSpecies: boolean;
  count: number;
  countPct: number;
  volume: number;
  volumePct: number | null;
  meanHeight: number | null;
  meanDbh: number | null;
  volumePerHa: number | null;
}

function row(genus: string, isSpecies: boolean, list: Tree[], all: number, allVolume: number, area: Area): SpeciesRow {
  const volume = sumVolume(list);
  return {
    genus,
    isSpecies,
    count: list.length,
    countPct: all ? (list.length / all) * 100 : 0,
    volume,
    volumePct: allVolume > 0 ? (volume / allVolume) * 100 : null,
    meanHeight: mean(valuesOf(list, 'height')),
    meanDbh: mean(valuesOf(list, 'dbh')),
    volumePerHa: perHa(volume, area),
  };
}

export function speciesTable(trees: Tree[], area: Area): { rows: SpeciesRow[]; total: SpeciesRow } {
  const groups = new Map<string, Tree[]>();
  for (const t of trees) {
    const list = groups.get(t.genus);
    if (list) list.push(t);
    else groups.set(t.genus, [t]);
  }
  const allVolume = sumVolume(trees);
  const rows = [...groups.entries()]
    .map(([genus, list]) => row(genus, !NON_SPECIES.has(genus), list, trees.length, allVolume, area))
    .sort((a, b) => b.count - a.count || a.genus.localeCompare(b.genus));
  return { rows, total: row('', false, trees, trees.length, allVolume, area) };
}

export function topSpecies(trees: Tree[], n: number, onlySpecies = false): string[] {
  const counts = new Map<string, number>();
  for (const t of trees) if (!onlySpecies || t.isSpecies) counts.set(t.genus, (counts.get(t.genus) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, n).map(([g]) => g);
}

export interface Strata {
  min: number;
  max: number;
  lo: number;
  hi: number;
  counts: Record<Stratum, number>;
}

export function stratumOf(h: number, s: Strata): Stratum {
  return h <= s.lo ? 'lower' : h <= s.hi ? 'middle' : 'upper';
}

/** Height layers (etáže): the height range cut in thirds. */
export function strata(trees: Tree[]): Strata | null {
  const hs = valuesOf(trees, 'height');
  if (!hs.length) return null;
  const [min, max] = extent(hs);
  const s: Strata = { min, max, lo: min + (max - min) / 3, hi: min + (2 * (max - min)) / 3, counts: { lower: 0, middle: 0, upper: 0 } };
  for (const h of hs) s.counts[stratumOf(h, s)] += 1;
  return s;
}

export const OTHER = 'other';

export interface StackedRow {
  from: number;
  to: number;
  counts: Record<string, number>;
}

export interface Stacked {
  /** Genera in chart order, then `other` if anything fell outside them. */
  series: string[];
  rows: StackedRow[];
}

export const HISTOGRAM_BINS: Record<Metric, number> = { height: 22, dbh: 20, volume: 22 };
const STACK_SPECIES = 8;

function stack(trees: Tree[], rows: StackedRow[], binOf: (t: Tree) => number | null): Stacked {
  const top = topSpecies(trees, STACK_SPECIES);
  let other = false;
  for (const t of trees) {
    const i = binOf(t);
    if (i === null) continue;
    const key = top.includes(t.genus) ? t.genus : OTHER;
    if (key === OTHER) other = true;
    rows[i]!.counts[key] = (rows[i]!.counts[key] ?? 0) + 1;
  }
  return { series: other ? [...top, OTHER] : top, rows };
}

/** Equal-width bins over the positive values, stacked by the most common species. */
export function histogram(trees: Tree[], metric: Metric, bins: number): Stacked {
  const vs = valuesOf(trees, metric).filter((v) => v > 0);
  if (!vs.length) return { series: [], rows: [] };
  const [min, max] = extent(vs);
  const width = (max - min) / bins || 1;
  const rows = Array.from({ length: bins }, (_, i) => ({ from: min + i * width, to: i === bins - 1 ? max : min + (i + 1) * width, counts: {} }));
  return stack(trees, rows, (t) => {
    const v = metricOf(t, metric);
    return v === null || v <= 0 ? null : Math.min(bins - 1, Math.floor((v - min) / width));
  });
}

export const DBH_CLASS_EDGES = [0, 10, 20, 30, 40, 50];

/** The page's one DBH class scheme, in cm: <10, 10–20, …, 50+. */
export function dbhClasses(trees: Tree[]): Stacked {
  const rows = DBH_CLASS_EDGES.map((from, i) => ({ from, to: DBH_CLASS_EDGES[i + 1] ?? Infinity, counts: {} }));
  return stack(trees, rows, (t) => {
    if (t.dbhCm === null || t.dbhCm < 0) return null;
    const i = DBH_CLASS_EDGES.findIndex((from, j) => t.dbhCm! >= from && t.dbhCm! < (DBH_CLASS_EDGES[j + 1] ?? Infinity));
    return i < 0 ? 0 : i;
  });
}

/**
 * Figures the sections take ready-made when they come from a forest summary rather than from
 * every tree (summary.ts); without them the sections compute them from `trees`.
 */
export interface Derived {
  histograms: Record<Metric, Stacked>;
  dbhClasses: Stacked;
  ranges: Record<Metric, [number, number] | null>;
  maturity: (overrides: Threshold[]) => MaturityStats;
  /** `trees` is every n-th tree only (a thinned tile zoom); counts over it are scaled by this. */
  sample: number;
}

/** What does not depend on the maturity targets: recomputed only when the data changes. */
export interface BaseStats {
  trees: Tree[];
  area: Area;
  summary: Summary;
  species: { rows: SpeciesRow[]; total: SpeciesRow };
  strata: Strata | null;
  derived?: Derived;
}

export interface TreeStats extends BaseStats {
  maturity: MaturityStats;
  thresholds: Record<string, Target>;
}

export function baseStats(fc: FeatureCollection, boundary: FeatureCollection | null, hullHa?: number): BaseStats {
  const trees = normalize(fc);
  const area = areaOf(fc, boundary, hullHa);
  return { trees, area, summary: summary(trees, area), species: speciesTable(trees, area), strata: strata(trees) };
}

export function withMaturity(base: BaseStats, overrides: Threshold[]): TreeStats {
  return { ...base, maturity: base.derived ? base.derived.maturity(overrides) : maturity(base.trees, overrides), thresholds: thresholdsFor(overrides) };
}

export function computeTreeStats(fc: FeatureCollection, boundary: FeatureCollection | null, overrides: Threshold[], hullHa?: number): TreeStats {
  return withMaturity(baseStats(fc, boundary, hullHa), overrides);
}
